import { useCallback } from 'react';
import { AlertTriangle, Loader2, Trash2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import type { ApplicationUser } from '@/types';

interface RemoveUserDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: ApplicationUser | null;
  onConfirm: (userId: string) => void;
  isRemoving?: boolean | undefined;
}

export function RemoveUserDialog({
  open,
  onOpenChange,
  user,
  onConfirm,
  isRemoving,
}: RemoveUserDialogProps) {
  const handleConfirm = useCallback(() => {
    if (!user) return;
    onConfirm(user.id);
  }, [user, onConfirm]);

  if (!user) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[450px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-destructive">
            <Trash2 className="h-5 w-5" />
            Remove User
          </DialogTitle>
          <DialogDescription>
            This will remove {user.displayName} from the application.
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          {/* User Info */}
          <div className="p-3 border rounded-lg bg-muted/30 mb-4">
            <p className="font-medium">{user.displayName}</p>
            <p className="text-sm text-muted-foreground">{user.email}</p>
          </div>

          {/* Warning */}
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              <p className="font-medium">This action cannot be undone.</p>
              <ul className="mt-2 list-disc list-inside text-sm space-y-1">
                <li>The user will immediately lose access to the application</li>
                <li>Their past actions will remain in the audit log</li>
                <li>You can re-add them later if needed</li>
              </ul>
            </AlertDescription>
          </Alert>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isRemoving}
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleConfirm}
            disabled={isRemoving}
          >
            {isRemoving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Remove User
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
